import type { LearningChangeType } from 'definitions';
import type { Experiment } from 'types';
import type { LibraryExperiment } from '@@/api/websites/get-website-experiments';
import type { ProgramOutcome } from '@@/api/program';
import type { LibraryFilters, LibraryRow } from './library-learnings';
import { useState, startTransition } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { useQueryState } from 'hooks/useQueryState';
import { useWebsites } from '@/state/useWebsites';
import { EXPERIMENT_STATUSES, LEARNING_CHANGE_TYPES } from 'definitions';
import { PROGRAM_OUTCOMES } from '@@/api/program/classify-outcome';
import { acceptFacetValue, elementFacets, liveThemesOf, themeFacets } from '../learnings/library-element-theme';
import {
  changeTypeFacets,
  matchesFilters,
  mechanismFacets,
  outcomeFacets,
  toLibraryRow,
  WRITTEN_IN_MECHANISM,
} from './library-learnings';
import { PREDEFINED_MECHANISMS } from './expected-mechanisms';
import { useOpenRequest } from './useOpenRequest';

// QA sits after Draft in the pills, though it is last in EXPERIMENT_STATUSES (appended there
// so the PostgreSQL enum only gains a value).
const STATUS_VIEWS = EXPERIMENT_STATUSES.flatMap((status) =>
  status === 'QA' ? [] : status === 'Draft' ? ['Draft', 'QA'] : [status],
);
const VIEWS = [...STATUS_VIEWS, 'All', 'Archived'];
export type ExperimentListViews = (typeof VIEWS)[number];

/**
 * One entry per view pill, so a pill can never look up a count that is not there.
 * `All` and `Archived` are not statuses: they are the two views that cut across them.
 */
export type ExperimentCounts = Record<(typeof EXPERIMENT_STATUSES)[number] | 'All' | 'Archived', number>;

export const useExperimentsList = () => {
  const [view, setView] = useQueryState<ExperimentListViews>('view', 'All');
  const [sortBy, setSortBy] = useQueryState<keyof Experiment>('sortBy', 'status');
  const [direction, setDirection] = useQueryState<'asc' | 'desc'>('direction', 'desc');
  const [searchText, setSearchText] = useQueryState<string>('searchText', '');
  const [page, setPage] = useState<number>(0);
  // null, not '', for "no filter": useQueryState only drops a key for null, so '' left
  // ?outcome=&mechanism= on every view click.
  const [outcomeParam, setOutcome] = useQueryState<string | null>('outcome', null);
  const [mechanismParam, setMechanism] = useQueryState<string | null>('mechanism', null);
  const [changeTypeParam, setChangeType] = useQueryState<string | null>('changeType', null);
  const [elementParam, setElement] = useQueryState<string | null>('element', null);
  const [themeParam, setTheme] = useQueryState<string | null>('theme', null);
  // A deep link to one test's learnings (?open=<experimentId>): the Library opens that row, scrolls
  // to it, and drops the key once it has. Read once, at mount, so it serves a link from another
  // screen or a reload; a link clicked on this screen goes through useOpenRequest instead.
  const [openParam, setOpenParam] = useQueryState<string | null>('open', null);
  const [openRequest, setOpenRequest] = useOpenRequest();
  // Which rows show their learnings. Held here rather than in the table so it survives
  // paging, sorting and a refetch.
  const [openIds, setOpenIds] = useState<number[]>([]);
  const [layout, setLayout] = useQueryState<string>('layout', 'table');

  const apiClient = useClient();
  const { website } = useWebsites();
  const websiteId = website?.id || '';

  /*
    isLoading and isError both leave the hook, because neither is derivable downstream.
    The query is gated on a website, and React Query reports a disabled query as
    isLoading false with no data — identical to a website that genuinely has no
    experiments, and identical again to a request that failed. The library screen has to
    tell those three apart, so the flags travel with the data.
  */
  const {
    data: allExperiments,
    isLoading,
    isError,
    refetch,
    // Explicit generics: apiClient.get collapses to `any`, which `select` would pass on.
  } = useQuery<LibraryExperiment[], Error, LibraryRow[]>({
    // Its own key under the shared prefix: only the Library asks for readings, and every
    // invalidateQueries(['EXPERIMENTS']) still reaches it.
    queryKey: ['EXPERIMENTS', websiteId, 'readings'],
    enabled: !!websiteId,
    queryFn: async () => apiClient.get<LibraryExperiment[]>(`/websites/${websiteId}/experiments?readings=1`),
    // A stable reference: an inline arrow is a new function each render, and React Query
    // re-runs select whenever it changes, so every keystroke rebuilt every row.
    select: toLibraryRows,
    // Home tells someone their experiment is waiting for its first visitor, so the list
    // has to notice when one arrives. Only polled while something is in that state.
    refetchInterval: (query) => (query.state.data?.some(isAwaitingFirstVisitor) ? FIRST_VISITOR_POLL_MS : false),
  });

  // Narrowed rather than cast: a stale or hand-edited ?outcome= would otherwise filter
  // every row out with no option shown as selected to explain why.
  const outcome = outcomeParam ?? '';
  const validOutcome = (PROGRAM_OUTCOMES as readonly string[]).includes(outcome) ? (outcome as ProgramOutcome) : '';
  // Likewise for a renamed or hand-edited mechanism id.
  const mechanism = MECHANISM_IDS.has(mechanismParam ?? '') ? mechanismParam! : '';
  // And for a change type that was renamed or never existed.
  const changeType = (LEARNING_CHANGE_TYPES as readonly string[]).includes(changeTypeParam ?? '')
    ? (changeTypeParam as LearningChangeType)
    : '';
  // Element and theme values are text the model wrote, so a URL value is accepted only when it is
  // one the loaded rows carry: a crafted or stale one becomes no filter, not an empty table.
  const liveThemes = liveThemesOf(allExperiments ?? []);
  const element = acceptFacetValue(elementParam, elementFacets(allExperiments ?? []));
  const theme = acceptFacetValue(themeParam, themeFacets(allExperiments ?? [], liveThemes));
  const filters: LibraryFilters = { outcome: validOutcome, mechanism, changeType, element, theme };
  const hasLearningFilter = !!filters.outcome || !!mechanism || !!changeType || !!element || !!theme;

  // A positive whole number or nothing: anything else in ?open= is ignored, not trusted. A request
  // made on this screen wins over the URL it was loaded with.
  const openTarget = openRequest ?? (/^\d+$/.test(openParam ?? '') ? Number(openParam) : null);

  /**
   * Switching views clears the outcome and mechanism filters. Kept across views they
   * silently emptied the next one: Running and Draft rows have no outcome, so "Won"
   * carried over to Running left a blank table with nothing on screen to explain it.
   */
  const changeView = (next: ExperimentListViews) => {
    setView(next);
    setOutcome(null);
    setMechanism(null);
    setChangeType(null);
    setElement(null);
    setTheme(null);
  };

  const toggleOpen = (id: number) =>
    setOpenIds((ids) => (ids.includes(id) ? ids.filter((open) => open !== id) : [...ids, id]));
  const isOpen = (id: number) => openIds.includes(id);
  const openRow = (id: number) => setOpenIds((ids) => (ids.includes(id) ? ids : [...ids, id]));

  const handleSortChange = (sortKey: keyof Experiment) => {
    const dir = sortKey !== sortBy ? 'desc' : direction === 'asc' ? 'desc' : 'asc';
    startTransition(() => {
      setSortBy(sortKey);
      setDirection(dir);
    });
  };

  const handleSearch = (text: string) => {
    setSearchText(text);
  };

  const resetFilters = () => {
    localStorage.removeItem(lsKeySearchText);
    localStorage.removeItem(lsKeySortBy);
    localStorage.removeItem(lsKeySortDirection);
    setDirection('desc');
    setSortBy('status');
    setSearchText('');
  };

  // A stale ?layout=patterns (the layout the Learnings page replaced) reads as the table.
  const isTimeline = layout === 'timeline';

  // The view alone, before the learnings filters, which the facet counts start from. The
  // timeline has no views: it draws every experiment, archived ones included, because on
  // a time axis the dates already separate past, present and future, and a status view
  // there only hid the history the timeline exists to show.
  const inView = (() => {
    if (!allExperiments) return [];
    if (isTimeline) return allExperiments;
    return view === 'All'
      ? allExperiments.filter((exp) => !exp.archived)
      : view === 'Archived'
        ? allExperiments.filter((exp) => !!exp.archived)
        : allExperiments.filter((exp) => exp.status === view && !exp.archived);
  })();

  // Each group counts over the view narrowed by the *other* group, so with a mechanism
  // selected "Won 2" means clicking Won leaves two rows. The search box is applied later,
  // inside the table, and is not reflected in the counts.
  const outcomes = outcomeFacets(inView.filter((row) => matchesFilters(row, { ...filters, outcome: '' })));
  const mechanisms = mechanismFacets(inView.filter((row) => matchesFilters(row, { ...filters, mechanism: '' })));
  const changeTypes = changeTypeFacets(inView.filter((row) => matchesFilters(row, { ...filters, changeType: '' })));
  const elements = elementFacets(inView.filter((row) => matchesFilters(row, { ...filters, element: '' })));
  const themes = themeFacets(
    inView.filter((row) => matchesFilters(row, { ...filters, theme: '' })),
    liveThemes,
  );

  const theExperiments = (() => {
    if (!allExperiments) return [];
    const experiments = (
      view === 'All'
        ? allExperiments.filter((exp: any) => !exp.archived)
        : view === 'Archived'
          ? allExperiments.filter((exp: any) => !!exp.archived)
          : allExperiments.filter((exp: any) => exp.status === view && !exp.archived)
    )
      .filter((exp) => matchesFilters(exp, filters))
      .sort((a: any, b: any) => {
        if (sortBy === 'status') {
          if (direction === 'asc') {
            return statusSortMap[b.status] - statusSortMap[a.status];
          } else {
            return statusSortMap[a.status] - statusSortMap[b.status];
          }
        }

        if (direction === 'asc') {
          return a[sortBy] < b[sortBy] ? 1 : -1;
        } else {
          return a[sortBy] > b[sortBy] ? 1 : -1;
        }
      });

    return searchText
      ? experiments.filter((exp: any) => {
          const regex = new RegExp(searchText, 'i');
          return regex.test(exp.name);
        })
      : experiments;
  })();

  const counts: ExperimentCounts | null = (() => {
    if (!allExperiments) return null;

    // Counted per status rather than accumulated in one pass: the old loop did
    // `counts[exp.status]++` on a partial map, so any status not listed there turned the
    // whole pill label into NaN instead of leaving one count wrong.
    const live = allExperiments.filter((exp: any) => !exp.archived);
    const withStatus = (status: string) => live.filter((exp: any) => exp.status === status).length;

    return {
      All: live.length,
      Archived: allExperiments.length - live.length,
      Running: withStatus('Running'),
      Deployed: withStatus('Deployed'),
      Ended: withStatus('Ended'),
      Draft: withStatus('Draft'),
      QA: withStatus('QA'),
    };
  })();

  /**
   * Whether this website has ever had an experiment, archived ones included.
   *
   * What decides the first-run explainer, and a different question from `counts.All`:
   * archiving the last experiment should not put the "what is an experiment" screen
   * back on a website that has run a dozen.
   */
  const totalCount = allExperiments?.length ?? 0;

  /*
    The two sentences DataTable cannot write for itself.

    The view pill filters here, upstream of `useDataControl`, so an excluded view
    reaches the table as an empty set — its own guard sees `rawData.length === 0` and
    falls back to "No Data Found.", which reads as "this website has no experiments"
    when the truth is "nothing on this website is running". Both messages therefore
    name the view that did the excluding.
  */
  const viewEmptyMessage = (() => {
    // First: these filters run upstream of the table, so without this an empty result
    // read as the view's message ("Every experiment on this website is archived").
    if (hasLearningFilter) {
      const names = [
        filters.outcome && 'outcome',
        mechanism && 'mechanism',
        changeType && 'change type',
        element && 'element',
        theme && 'theme',
      ];
      return `No experiments in this view match the selected ${joinAnd(names)}.`;
    }
    if (searchText) return `Nothing matches "${searchText}".`;
    if (view === 'Archived') return 'Nothing is archived.';
    if (view === 'All') return 'Every experiment on this website is archived.';
    const live = counts?.All ?? 0;
    return `No ${view.toLowerCase()} experiments — ${live} on this website.`;
  })();

  // Undefined on the All view, where DataTable's own default is better: it quotes the
  // query back, and there is no upstream filter left to name.
  const viewNoMatchMessage = view === 'All' ? undefined : `No ${view.toLowerCase()} experiments match your search.`;

  return {
    experiments: theExperiments,
    counts,
    isLoading,
    isError,
    refetch,
    hasWebsite: !!websiteId,
    /** The selected website, or null. An open request belongs to the one it was made on. */
    websiteId: websiteId || null,
    totalCount,
    viewEmptyMessage,
    viewNoMatchMessage,
    sortBy,
    direction,
    sort: handleSortChange,
    VIEWS,
    view,
    visitorCounts: [],
    searchText,
    setView: changeView,
    page,
    setPage,
    handleSortChange,
    handleSearch,
    resetFilters,
    handlePageChange: setPage,
    outcome: filters.outcome,
    setOutcome,
    mechanism,
    setMechanism,
    outcomes,
    mechanisms,
    changeType: filters.changeType,
    setChangeType,
    changeTypes,
    element: filters.element,
    setElement,
    elements,
    theme: filters.theme,
    setTheme,
    themes,
    hasLearningFilter,
    clearLearningFilters: () => {
      setOutcome(null);
      setMechanism(null);
      setChangeType(null);
      setElement(null);
      setTheme(null);
    },
    isOpen,
    toggleOpen,
    openRow,
    /** The experiment ?open= asks for, or null. */
    openTarget,
    /** Drops ?open= and any request made on this screen, once the Library has acted on it. */
    consumeOpen: () => {
      setOpenParam(null);
      setOpenRequest(null);
    },
    /** Every experiment, unfiltered: the timeline needs all of a pipeline stage's members. */
    allRows: allExperiments ?? [],
    /** What the timeline draws: every experiment, narrowed only by the outcome and mechanism filter. */
    timelineRows: inView.filter((row) => matchesFilters(row, filters)),
    layout: isTimeline ? ('timeline' as const) : ('table' as const),
    setLayout,
  };
};

/** "a", "a and b", "a, b and c". */
const joinAnd = (items: readonly (string | false | '')[]): string => {
  const names = items.filter((item): item is string => !!item);
  return names.length < 2 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

const MECHANISM_IDS = new Set<string>([...PREDEFINED_MECHANISMS.map((m) => m.id), WRITTEN_IN_MECHANISM]);

const toLibraryRows = (experiments: LibraryExperiment[]): LibraryRow[] => experiments.map(toLibraryRow);

const FIRST_VISITOR_POLL_MS = 60_000;

const isAwaitingFirstVisitor = (experiment: LibraryExperiment) =>
  experiment.status === 'Running' && Number(experiment.sessions ?? 0) === 0;

const statusSortMap: any = {
  Running: 0,
  QA: 1,
  Draft: 1,
  Ended: 2,
};

const lsKeySortBy = 'PERTENTO_EXPERIMENTS_SORTBY';
const lsKeySortDirection = 'PERTENTO_EXPERIMENTS_SORT_DIRECTION';
const lsKeySearchText = 'PERTENTO_EXPERIMENTS_SEARCH_TEXT';
